import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { CalendarDateIcon } from '../../../../web/components/svgs/icons/CalendarDateIcon';
import { DownloadIconV2 } from '../../../../web/components/svgs/icons/DownloadIconV2';
import { PhoneIcon } from '../../../../web/components/svgs/icons/PhoneIcon';
import { ShareIcon } from '../../../../web/components/svgs/icons/ShareIcon';
import { UsersIcon } from '../../../../web/components/svgs/icons/UsersIcon';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { BasicPage } from '../../BasicPage';
import { UtilityCard } from './components/UtilityCard';
import { ICON_SIZE_PX, styleSheet } from './styles';

interface IProps {
	className?: string;
}

export const Utilities: React.FC<IProps> = observer(({ className = '' }) => {
	const [spinners, setSpinners] = useState({
		FollowUps: false,
		Leads: false,
		ProfileImages: false,
		TeamMeeting: false,
		TwilioNumbers: false,
	});

	const userSession = useUserSession();
	const inputRef = useRef<HTMLInputElement>(null);

	const [fileUploadType, setFileUploadType] = useState<'TeamMeeting' | 'TwilioNumbers'>(null);

	const loadPics = async () => {
		setSpinners(prev => ({ ...prev, ProfileImages: true }));
		const result = await userSession.webServiceHelper.callWebServiceAsync('aida/loadProfilePicsFromGoogle', 'PUT');
		setSpinners(prev => ({ ...prev, ProfileImages: false }));

		if (result.success) {
			toast.success('Loaded pictures for all connected users');
		} else {
			toast.error(result.systemMessage || 'Failed to load pictures');
		}
	};

	const downloadFile = async () => {
		setSpinners(prev => ({ ...prev, FollowUps: true }));
		await userSession.webServiceHelper.callWebServiceRawResponse(
			'lead/followUps/reportNow',
			'GET',
			null,
			response => {
				response.blob().then(blob => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = `COFFEE-FUs.csv`;
					document.body.appendChild(a);
					a.click();
					a.remove();
					setSpinners(prev => ({ ...prev, FollowUps: false }));
				});
			},
			error => {
				setSpinners(prev => ({ ...prev, FollowUps: false }));

				toast.error(error.systemMessage || 'Download failed');
			}
		);
	};

	const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.currentTarget.files[0];
		if (!file) {
			return; // cancelled the file picker
		}

		const uploadType = fileUploadType;
		setSpinners(prev => ({ ...prev, [uploadType]: true }));

		inputRef.current.value = '';
		const formData = new FormData();
		formData.append('files', file);
		// make request

		const uploadRoute = fileUploadType === 'TeamMeeting' ? 'aida/teamMeetingConfigs' : 'aida/telephonyConfigs';
		const successMessage =
			fileUploadType === 'TeamMeeting' ? 'Uploaded meeting configuration' : 'Uploaded twilio configuration';
		const result = await userSession.webServiceHelper.callWebServiceAsync(uploadRoute, 'PUT', formData);
		setSpinners(prev => ({ ...prev, [uploadType]: false }));

		if (result.success) {
			toast.success(successMessage);
		} else {
			toast.error(result.systemMessage || 'Upload failed');
		}
	};

	const onUploadMeetingConfig = () => {
		setFileUploadType('TeamMeeting');
		inputRef.current?.click();
	};

	const onUploadTwilioConfig = () => {
		setFileUploadType('TwilioNumbers');
		inputRef.current?.click();
	};

	const exportLeads = async () => {
		setSpinners(prev => ({ ...prev, Leads: true }));
		const result = await userSession.webServiceHelper.callWebServiceAsync('lead/export', 'POST');
		setSpinners(prev => ({ ...prev, Leads: false }));
		if (result.success) {
			toast.success('You will receive an email when your export is ready to be downloaded.');
		} else {
			toast.error(result.systemMessage || 'Export failed');
		}
	};

	const allowExport = userSession.account.features.aida?.allowExport;

	return (
		<BasicPage className={`${css(styleSheet.utilities)} ${className}`} title='Utilities' appearance='light'>
			<div className={css(styleSheet.grid)}>
				<UtilityCard
					buttonLabel='Load All Profile Pictures from Google'
					icon={<UsersIcon width={ICON_SIZE_PX} height={ICON_SIZE_PX} />}
					isLoading={spinners.ProfileImages}
					onClick={loadPics}
				/>

				<UtilityCard
					buttonLabel='Download Follow Ups'
					icon={<DownloadIconV2 width={ICON_SIZE_PX} height={ICON_SIZE_PX} />}
					isLoading={spinners.FollowUps}
					onClick={downloadFile}
				/>

				<UtilityCard
					buttonLabel='Upload Meeting Configuration'
					icon={<CalendarDateIcon width={ICON_SIZE_PX} height={ICON_SIZE_PX} fillColor={THEME.colors.icon.primary} />}
					isLoading={spinners.TeamMeeting}
					onClick={onUploadMeetingConfig}
				/>

				<UtilityCard
					buttonLabel='Upload Twilio Configuration'
					icon={<PhoneIcon width={ICON_SIZE_PX} height={ICON_SIZE_PX} fill={THEME.colors.icon.primary} />}
					isLoading={spinners.TwilioNumbers}
					onClick={onUploadTwilioConfig}
				/>

				{allowExport && (
					<UtilityCard
						buttonLabel='Export Leads'
						icon={<ShareIcon width={ICON_SIZE_PX} height={ICON_SIZE_PX} fillColor={THEME.colors.icon.primary} />}
						isLoading={spinners.Leads}
						onClick={exportLeads}
					/>
				)}
				<input
					type='file'
					className={css(styleSheet.fileInput)}
					onChange={onFileChange}
					ref={inputRef}
					accept='.xlsx,.csv'
				/>
			</div>
		</BasicPage>
	);
});
